/**
 * useAssignContractor — thin React hook over the canonical
 * assignContractor write boundary.
 *
 * Use for any DISPATCHER/ADMIN action that assigns or unassigns a contractor
 * outside the offer-accept flow (manual assignment, unassign, reassign).
 *
 * For offer-driven assignment, use useMarketplaceOffers().accept().
 *
 * source rules:
 *   - "manual"       — dispatcher assigning without an offer (emits manual_assignment)
 *   - "offer_accept" — reserved for offerEngine path (do NOT call directly)
 */

import { useCallback } from "react";
import { useAppData } from "@/data/AppDataContext";
import { assertContractorAssignable } from "@/data/payProfileGuard";
import {
  assignContractor,
  type AssignContractorInput,
  type AssignContractorResult,
} from "@/data/contractor/assignContractor";

export function useAssignContractor() {
  const { setJobs, jobs, contractors, payProfiles, appendSystemEvents } = useAppData();

  return useCallback(
    (input: Omit<AssignContractorInput, "companyName"> & { companyName?: string }): AssignContractorResult => {
      let result: AssignContractorResult = { jobs: [], events: [], changed: false };
      const job = jobs.find((j) => j.id === input.jobId);
      if (!job) return result;

      // Enforce pay-profile eligibility at the canonical assignment write boundary.
      // This prevents bypasses when callers skip pre-checks.
      if (input.contractorId) {
        const contractor = contractors.find((c) => c.id === input.contractorId);
        const guard = assertContractorAssignable({
          contractor,
          contractorName: input.companyName,
          contractors,
          projectType: job.projectType,
          payProfiles,
        });
        if (!guard.allowed) return result;
      }

      const companyName =
        input.companyName ??
        (input.contractorId
          ? contractors.find((c) => c.id === input.contractorId)?.companyName
          : undefined);
      setJobs((prev) => {
        result = assignContractor(prev, { ...input, companyName });
        return result.changed ? result.jobs : prev;
      });
      if (result.events.length) appendSystemEvents(result.events);
      return result;
    },
    [setJobs, jobs, contractors, payProfiles, appendSystemEvents],
  );
}
